// 戶的詳情 ›「家戶成員」卡（vms#33；home-base#69：Albert 要在網頁上被加成家戶管理員）。
//   清單：GET /v1/homes/{id}/members（名字 / email、角色）。
//   〔加入成員〕：從組織成員挑 active、還不在這一戶的人 + 角色 → POST /v1/orgs/{org}/homes/{home}/members；同一支改角色。
//   移出：DELETE /v1/homes/{id}/members/{user}（跟著組織角色的人沒有可拿掉的，不畫〔移出〕）。
//   權限照 App：owner 給管理員 / 成員 / 訪客、admin 給成員 / 訪客，其他人只看清單（UX-11）。
//   REAUTH_REQUIRED 由 SDK 交給 StepUpHost 重新驗證後重送（AUTH-05）；EMAIL_NOT_VERIFIED 開「先驗證你的 email」（vms#31）。
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useConfirm } from "../../components/ConfirmDialog";
import { useEmailVerifyPrompt } from "../../components/EmailVerifyPrompt";
import { orgApi, type MyOrg } from "../../lib/planeApi";
import { treeApi, type HomeMember } from "../../lib/orgTreeApi";
import {
  addableOrgMembers,
  assignableHomeRoles,
  canManageHomeMember,
  canRemoveHomeMember,
  memberLabel,
  type HomeRole,
} from "./homeMembers";
import { otErr } from "./otErr";

export function HomeMembersCard({
  org,
  homeId,
  homeName,
}: {
  org: MyOrg;
  homeId: string;
  homeName: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const key = ["ot", org.org_id, "home-members", homeId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => treeApi.homeMembers(org, homeId),
    retry: false,
  });
  const myRole = q.data?.role ?? "";
  const roles = assignableHomeRoles(myRole);
  const orgMembers = useQuery({
    queryKey: ["ot", org.org_id, "org-members"],
    queryFn: () => orgApi.members(org),
    enabled: roles.length > 0,
    retry: false,
  });
  const [adding, setAdding] = useState(false);
  const [pick, setPick] = useState("");
  const [role, setRole] = useState<HomeRole>("member");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();
  const ev = useEmailVerifyPrompt();

  const members = q.data?.members ?? [];
  const candidates = addableOrgMembers(orgMembers.data ?? [], members);
  const roleText = (r: string) => t("people.hrole_" + r, { defaultValue: r });

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      await fn();
      setInfo(done);
      await qc.invalidateQueries({ queryKey: key });
      return true;
    } catch (e) {
      if (!ev.ask(e)) setErr(otErr(t, e));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function add() {
    const m = candidates.find((c) => c.user_id === pick);
    if (!m) return;
    const ok = await run(
      () => orgApi.addHomeMember(org, homeId, { user_id: m.user_id, role }),
      t("ot.hm_added", { name: memberLabel(m), role: roleText(role) }),
    );
    if (ok) {
      setAdding(false);
      setPick("");
    }
  }

  async function changeRole(m: HomeMember, r: HomeRole) {
    if (r === m.role) return;
    await run(
      () => orgApi.addHomeMember(org, homeId, { user_id: m.user_id, role: r }),
      t("ot.hm_role_changed", { name: memberLabel(m), role: roleText(r) }),
    );
  }

  async function remove(m: HomeMember) {
    const name = memberLabel(m);
    const ok = await ask({
      title: t("ot.hm_remove_title", { name, home: homeName }),
      body: t("ot.hm_remove_body", { name }),
      action: t("ot.hm_remove"),
      danger: true,
    });
    if (!ok) return;
    await run(
      () => treeApi.removeHomeMember(org, homeId, m.user_id),
      t("ot.hm_removed", { name }),
    );
  }

  return (
    <section className="card" data-testid="home-members">
      <h4>{t("ot.hm_title")}</h4>
      {q.isLoading && <p className="muted small">{t("common.loading")}</p>}
      {q.isError && <p className="error small">{otErr(t, q.error)}</p>}
      {err && (
        <p className="error small" data-testid="home-members-error">
          {err}
        </p>
      )}
      {info && (
        <p className="small" data-testid="home-members-info">
          {info}
        </p>
      )}
      {q.data && (
        <ul className="plain-list" data-testid="home-members-list">
          {members.map((m) => {
            const manage = canManageHomeMember(myRole, m) && roles.length > 0;
            return (
              <li
                key={m.user_id}
                className="row"
                data-testid={"home-member-" + m.user_id}
              >
                <div style={{ flex: 1 }}>
                  <strong>{memberLabel(m)}</strong>
                  {m.is_me && (
                    <span className="muted small"> {t("ot.hm_you")}</span>
                  )}
                  {m.email && memberLabel(m) !== m.email && (
                    <div className="muted small">{m.email}</div>
                  )}
                  {m.role_source === "default" && (
                    <div className="muted small">{t("ot.hm_default_role")}</div>
                  )}
                </div>
                {manage ? (
                  <select
                    className="input"
                    style={{ width: "auto" }}
                    value={m.role}
                    disabled={busy}
                    aria-label={t("people.role_of", { name: memberLabel(m) })}
                    data-testid={"home-member-role-" + m.user_id}
                    onChange={(e) =>
                      void changeRole(m, e.target.value as HomeRole)
                    }
                  >
                    {!roles.includes(m.role as HomeRole) && (
                      <option value={m.role}>{roleText(m.role)}</option>
                    )}
                    {roles.map((r) => (
                      <option key={r} value={r}>
                        {roleText(r)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="ot-chip">{roleText(m.role)}</span>
                )}
                {canRemoveHomeMember(myRole, m) && (
                  <button
                    className="btn btn-sm btn-danger-outline"
                    disabled={busy}
                    data-testid={"home-member-remove-" + m.user_id}
                    onClick={() => void remove(m)}
                  >
                    {t("ot.hm_remove")}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {roles.length > 0 && !adding && (
        <button
          className="btn btn-sm"
          data-testid="home-member-add"
          disabled={busy}
          onClick={() => setAdding(true)}
        >
          {t("ot.hm_add")}
        </button>
      )}
      {roles.length > 0 && adding && (
        <div className="row" data-testid="home-member-add-form">
          {orgMembers.isLoading ? (
            <span className="muted small">{t("common.loading")}</span>
          ) : candidates.length === 0 ? (
            <span className="muted small">{t("ot.hm_no_candidates")}</span>
          ) : (
            <select
              className="input"
              style={{ width: "auto" }}
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              aria-label={t("ot.hm_pick")}
              data-testid="home-member-pick"
            >
              <option value="">{t("ot.hm_pick")}</option>
              {candidates.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {memberLabel(m)}
                  {m.email && memberLabel(m) !== m.email ? ` (${m.email})` : ""}
                </option>
              ))}
            </select>
          )}
          <select
            className="input"
            style={{ width: "auto" }}
            value={role}
            onChange={(e) => setRole(e.target.value as HomeRole)}
            aria-label={t("people.role")}
            data-testid="home-member-add-role"
          >
            {roles.map((r) => (
              <option key={r} value={r}>
                {roleText(r)}
              </option>
            ))}
          </select>
          <button
            className="btn btn-sm btn-primary"
            disabled={busy || !pick}
            data-testid="home-member-add-submit"
            onClick={() => void add()}
          >
            {t("ot.hm_add_submit")}
          </button>
          <button
            className="btn btn-sm"
            disabled={busy}
            onClick={() => setAdding(false)}
          >
            {t("common.cancel")}
          </button>
        </div>
      )}
      {dialog}
      {ev.prompt}
    </section>
  );
}
